import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button, DatePicker, Input, PageHeader } from '@/shared/ui'
import type { InvoiceDTO, InvoiceListFilters } from '@/shared/api'
import {
  downloadInvoicePdf,
  useCancelInvoice,
  useConfirmInvoice,
  useDebounce,
  useInvoices,
  useLegalEntities,
  useOperations,
} from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { BalanceTable } from './BalanceTable'
import { ConfirmActionModal } from './ConfirmActionModal'
import { InvoiceCreateModal } from './InvoiceCreateModal'
import { OperationsTable } from './OperationsTable'
import { dayEndMsk, dayStartMsk, formatMoney, invoiceNumberLabel } from './format'

type BalanceTab = 'invoices' | 'operations'

const TABS: Array<{ value: BalanceTab; label: string }> = [
  { value: 'invoices', label: 'Счета' },
  { value: 'operations', label: 'Операции' },
]

const STATUS_OPTIONS = [
  { value: 0, label: 'Все' },
  { value: 1, label: 'Ожидает подтверждения' },
  { value: 2, label: 'Оплачен (демо)' },
  { value: 3, label: 'Аннулирован' },
]

const PAGE_SIZE = 20

interface ActionTarget {
  mode: 'confirm' | 'cancel'
  invoice: InvoiceDTO
}

export const BalancePage: React.FC = () => {
  const { hasPermission } = useAuth()

  if (!hasPermission('balance', 'view')) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <PageHeader title="Счета и операции" />
        <div className="p-6">
          <div className="admin-card p-8 text-center text-sm text-gray-500">
            Нет права на просмотр счетов и операций.
          </div>
        </div>
      </div>
    )
  }

  return <BalancePageContent />
}

const BalancePageContent: React.FC = () => {
  const { hasPermission } = useAuth()
  const canConfirm = hasPermission('balance', 'confirm_payment')
  const canCancel = hasPermission('balance', 'cancel_invoice')
  const canCreateInvoice = hasPermission('balance', 'create_invoice')
  const canViewOperations = hasPermission('balance', 'view_operations')
  const canViewLegalEntities = hasPermission('legal_entities', 'view')

  // Вкладка «Операции» строится по выбранному юрлицу: без права
  // `legal_entities.view` список юрлиц не запрашивается и выбрать компанию
  // нечем, поэтому вкладка недостижима — не показываем её вовсе.
  const tabs = TABS.filter(
    (item) => item.value === 'invoices' || canViewLegalEntities
  )

  const [tab, setTab] = useState<BalanceTab>('invoices')
  const [status, setStatus] = useState(0)
  const [legalEntityId, setLegalEntityId] = useState<number | ''>('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [invoicesPage, setInvoicesPage] = useState(1)
  const [operationsPage, setOperationsPage] = useState(1)

  // Право могли убрать у роли на ходу — возвращаемся на вкладку «Счета».
  useEffect(() => {
    if (!canViewLegalEntities && tab === 'operations') setTab('invoices')
  }, [canViewLegalEntities, tab])

  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const { data: entities } = useLegalEntities(
    { page: 1, limit: 100 },
    { enabled: canViewLegalEntities }
  )

  // Любое изменение фильтра сбрасывает страницу выборки.
  useEffect(() => {
    setInvoicesPage(1)
  }, [status, legalEntityId, debouncedSearch, dateFrom, dateTo])

  useEffect(() => {
    setOperationsPage(1)
  }, [legalEntityId])

  const invoiceFilters = useMemo<InvoiceListFilters>(() => {
    const filters: InvoiceListFilters = { page: invoicesPage, limit: PAGE_SIZE }
    if (status) filters.status = status
    if (legalEntityId !== '') filters.legalEntityId = legalEntityId
    if (debouncedSearch.trim()) filters.search = debouncedSearch.trim()
    if (dateFrom) filters.dateFrom = dayStartMsk(dateFrom)
    if (dateTo) filters.dateTo = dayEndMsk(dateTo)
    return filters
  }, [invoicesPage, status, legalEntityId, debouncedSearch, dateFrom, dateTo])

  const invoices = useInvoices(invoiceFilters, { enabled: tab === 'invoices' })

  const operationsEntityId = legalEntityId === '' ? 0 : legalEntityId
  const operations = useOperations(
    operationsEntityId,
    operationsPage,
    PAGE_SIZE,
    { enabled: tab === 'operations' && canViewOperations }
  )

  const confirmInvoice = useConfirmInvoice()
  const cancelInvoice = useCancelInvoice()

  const handleDownloadPdf = async (invoice: InvoiceDTO) => {
    setDownloadingId(invoice.invoiceId)
    try {
      await downloadInvoicePdf(invoice.invoiceId)
    } catch (error) {
      const apiError = error as { alreadyToasted?: boolean }
      if (!apiError?.alreadyToasted) toast.error('Не удалось скачать PDF счёта')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleActionSubmit = async (reason: string) => {
    if (!actionTarget) return
    try {
      if (actionTarget.mode === 'confirm') {
        await confirmInvoice.mutateAsync(actionTarget.invoice.invoiceId)
      } else {
        await cancelInvoice.mutateAsync({
          invoiceId: actionTarget.invoice.invoiceId,
          reason,
        })
      }
      setActionTarget(null)
    } catch {
      // Ошибку уже показал interceptor API — модалка остаётся открытой.
    }
  }

  const actionInvoiceLabel = actionTarget
    ? `${invoiceNumberLabel(actionTarget.invoice)} на сумму ${formatMoney(actionTarget.invoice.amount)}`
    : ''

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Счета и операции"
        description="Демо-баланс юрлиц: счета на пополнение и журнал операций"
      />

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {tabs.map((item) => (
              <button
                key={item.value}
                type="button"
                className={
                  tab === item.value
                    ? 'admin-button admin-button-primary'
                    : 'admin-button admin-button-secondary'
                }
                onClick={() => setTab(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {canCreateInvoice && (
            <Button onClick={() => setCreateOpen(true)}>Создать счёт</Button>
          )}
        </div>

        <div className="admin-card p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {canViewLegalEntities && (
              <div className="space-y-1 min-w-[240px]">
                <label className="block text-sm font-medium text-gray-700">Юрлицо</label>
                <select
                  className="admin-input"
                  value={legalEntityId}
                  onChange={(e) =>
                    setLegalEntityId(e.target.value ? Number(e.target.value) : '')
                  }
                >
                  <option value="">Все юрлица</option>
                  {(entities?.items ?? []).map((entity) => (
                    <option key={entity.legalEntityId} value={entity.legalEntityId}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {tab === 'invoices' && (
              <>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Статус</label>
                  <select
                    className="admin-input"
                    value={status}
                    onChange={(e) => setStatus(Number(e.target.value))}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-56">
                  <Input
                    label="Поиск"
                    placeholder="Номер счёта"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="w-40 space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Дата с</label>
                  <DatePicker value={dateFrom} onChange={setDateFrom} />
                </div>
                <div className="w-40 space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Дата по</label>
                  <DatePicker value={dateTo} onChange={setDateTo} />
                </div>
              </>
            )}
          </div>
        </div>

        {tab === 'invoices' ? (
          <BalanceTable
            data={invoices.data?.items ?? []}
            loading={invoices.isLoading}
            canConfirm={canConfirm}
            canCancel={canCancel}
            downloadingId={downloadingId}
            onConfirm={(invoice) => setActionTarget({ mode: 'confirm', invoice })}
            onCancel={(invoice) => setActionTarget({ mode: 'cancel', invoice })}
            onDownloadPdf={handleDownloadPdf}
            pagination={
              invoices.data
                ? {
                    page: invoices.data.page,
                    limit: invoices.data.limit,
                    total: invoices.data.total,
                    pages: invoices.data.pages,
                    onPageChange: setInvoicesPage,
                  }
                : undefined
            }
          />
        ) : !canViewOperations ? (
          <div className="admin-card p-8 text-center text-sm text-gray-500">
            Нет права на просмотр операций.
          </div>
        ) : operationsEntityId === 0 ? (
          <div className="admin-card p-8 text-center text-sm text-gray-500">
            Выберите юрлицо, чтобы увидеть историю операций.
          </div>
        ) : (
          <OperationsTable
            data={operations.data?.items ?? []}
            loading={operations.isLoading}
            pagination={
              operations.data
                ? {
                    page: operations.data.page,
                    limit: operations.data.limit,
                    total: operations.data.total,
                    pages: operations.data.pages,
                    onPageChange: setOperationsPage,
                  }
                : undefined
            }
          />
        )}
      </div>

      <InvoiceCreateModal
        isOpen={createOpen}
        entityId={
          legalEntityId === '' || !canViewLegalEntities ? undefined : legalEntityId
        }
        entityName={
          legalEntityId === ''
            ? undefined
            : (entities?.items ?? []).find(
                (entity) => entity.legalEntityId === legalEntityId
              )?.name
        }
        entities={canViewLegalEntities ? entities?.items ?? [] : []}
        onClose={() => setCreateOpen(false)}
      />

      <ConfirmActionModal
        isOpen={!!actionTarget}
        title={
          actionTarget?.mode === 'cancel'
            ? 'Аннулировать счёт'
            : 'Подтвердить оплату счёта'
        }
        description={
          actionTarget?.mode === 'cancel'
            ? `${actionInvoiceLabel}. Счёт перейдёт в статус «Аннулирован», баланс не изменится.`
            : `${actionInvoiceLabel}. Счёт перейдёт в статус «Оплачен (демо)», баланс компании пополнится.`
        }
        confirmLabel={actionTarget?.mode === 'cancel' ? 'Аннулировать' : 'Подтвердить оплату'}
        variant={actionTarget?.mode === 'cancel' ? 'danger' : 'primary'}
        requireReason={actionTarget?.mode === 'cancel'}
        loading={confirmInvoice.isPending || cancelInvoice.isPending}
        onClose={() => setActionTarget(null)}
        onSubmit={handleActionSubmit}
      />
    </div>
  )
}
